import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import MatchInfoDialog from "@/components/MatchInfoDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Target, Trophy, ArrowRight, Clock, Shield, MapPin } from "lucide-react";

const API = "/api";

function formatTime(dt) {
  if (!dt) return "TBD";
  const d = new Date(dt.replace(" ", "T"));
  if (isNaN(d.getTime())) return "TBD";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function TodaysMatches() {
  const { isAdmin } = useAuth();
  const [matches, setMatches] = useState([]);
  const [dateLabel, setDateLabel] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/events/today?t=${Date.now()}`);
        const events = res.data?.events || [];
        const flattened = [];
        events.forEach((ev) => {
          (ev.matches || []).forEach((m) => {
            flattened.push({ ...m, event_id: ev.id, event_name: ev.name });
          });
        });
        flattened.sort((a, b) => {
          const ta = a.start_time ? new Date(a.start_time.replace(" ", "T")).getTime() : Number.MAX_SAFE_INTEGER;
          const tb = b.start_time ? new Date(b.start_time.replace(" ", "T")).getTime() : Number.MAX_SAFE_INTEGER;
          return ta - tb;
        });
        setMatches(flattened);
        const dateStr = res.data?.date;
        if (dateStr) {
          const d = new Date(dateStr + "T00:00:00");
          setDateLabel(
            d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
          );
        }
      } catch (e) {
        console.error("Failed to load today's matches", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="todays-matches-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Today's Matches
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <Calendar size={16} />
            {dateLabel}
          </p>
        </div>
        <Link to="/today">
          <Button variant="outline">View Today's Events</Button>
        </Link>
      </div>

      {matches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-slate-700">No Matches Today</h3>
            <p className="text-slate-500 mt-2">There are no matches scheduled for today.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Clock className="text-[#1B3B6F]" />
              Schedule ({matches.length} matches)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {matches.map((match) => (
                <div
                  key={match.id}
                  className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-slate-50 transition-colors"
                  data-testid={`todays-match-${match.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center justify-center min-w-[72px] bg-[#1B3B6F]/10 text-[#1B3B6F] rounded-lg px-2 py-2">
                      <Clock size={14} />
                      <span className="font-mono font-semibold text-sm mt-1">{formatTime(match.start_time)}</span>
                    </div>
                    <div className={`p-2 rounded-lg ${match.is_protection ? "bg-purple-100" : "bg-green-100"}`}>
                      {match.is_protection ? (
                        <Shield className="text-purple-600" size={20} />
                      ) : (
                        <Target className="text-green-600" size={20} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">
                        <MatchInfoDialog match={match} eventName={match.event_name} prefix />
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{match.event_name}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                        {match.is_protection && (
                          <Badge variant="outline" className="text-purple-600">Protection</Badge>
                        )}
                        <span>{match.shots_per_round} shots</span>
                        {match.trap_number && (
                          <span className="flex items-center gap-1">
                            <MapPin size={10} /> Trap {match.trap_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:flex-shrink-0">
                    <Link to={`/leaderboard/${match.id}`}>
                      <Button variant="outline" size="sm" data-testid={`tm-leaderboard-${match.id}`}>
                        <Trophy size={14} className="mr-1" />
                        Leaderboard
                      </Button>
                    </Link>
                    {isAdmin() && (
                      <Link to={`/score-entry/${match.id}`}>
                        <Button size="sm" className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90" data-testid={`tm-score-entry-${match.id}`}>
                          Enter Scores
                          <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
